import { MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { TenantResource } from '@/api/requests/tenant/types';
import { OptionResource, PermissionResource, UserResource } from '@/api/requests/types';

export const mutations: MutationTree<RootState> = {
    /**
     * Set app loaded status
     *
     * @param state
     * @param isLoaded
     */
    setAppLoaded(state, isLoaded: boolean) {
        state.appLoaded = isLoaded;
    },

    /**
     * Set current tenant.
     * @param state
     * @param tenant
     */
    setTenant(state, tenant: TenantResource) {
        state.tenant = tenant;
    },

    /**
     * Reset current tenant in state.
     *
     * @param state
     */
    resetTenant(state) {
        state.tenant = undefined;
    },

    /**
     * Set authenticated user.
     * @param state
     * @param user
     */
    setUser(state, user: UserResource) {
        state.user = user;
    },

    /**
     * Reset auth user in state.
     *
     * @param state
     */
    resetUser(state) {
        state.user = undefined;
    },

    /**
     * Set permissions in state.
     * @param state
     * @param permissions
     */
    setPermissions(state, permissions: PermissionResource[]) {
        state.permissions = permissions;
    },

    /**
     * Reset permissions in state.
     *
     * @param state
     */
    resetPermissions(state) {
        state.permissions = [];
    },

    /**
     * Set a preference in state.
     * @param state
     * @param preference
     */
    setPreference(state, preference: OptionResource) {
        const index = state.preferences.findIndex((item) => item.key === preference.key);
        if (index > -1) {
            state.preferences.splice(index, 1, preference);
        } else {
            state.preferences.push(preference);
        }
    },

    /**
     * Set a setting in state.
     * @param state
     * @param setting
     */
    setSetting(state, setting: OptionResource) {
        const index = state.settings.findIndex((item) => item.key === setting.key);
        if (index > -1) {
            state.settings.splice(index, 1, setting);
        } else {
            state.settings.push(setting);
        }
    },

    /**
     * Add a generic model with it's route to the list of generic models.
     * @param state
     * @param params
     */
    addGenericModel(state, params: {genericModel: Object, genericModelRoute: string}) {
        state.genericModels.push(params.genericModel);
        state.genericModelRoutes.push(params.genericModelRoute);
    },

    /**
     * Remove last generic model with it's route to the list of generic models.
     * @param state
     */
    removeLastGenericModel(state) {
        state.genericModels.pop();
    },

    /**
     * Remove last generic model's route to the list of generic model routes.
     * @param state
     */
    removeLastGenericModelRoute(state) {
        state.genericModelRoutes.pop();
    },

    /**
     * Reset the list of generic models.
     * @param state
     */
    resetGenericModels(state) {
        state.genericModels = [];
    },

    /**
     * Reset the list of generic model routes.
     * @param state
     */
    resetGenericModelRoutes(state) {
        state.genericModelRoutes = [];
    },

    /**
     * Reset a preference in state.
     *
     * @param state
     * @param key
     */
    resetPreference(state, key: string) {
        state.preferences = state.preferences.filter((item) => item.key !== key);
    },

    /**
     * Reset a setting in state.
     *
     * @param state
     * @param key
     */
    resetSetting(state, key: string) {
        state.settings = state.settings.filter((item) => item.key !== key);
    },

    /**
     * Set preferences in state.
     * @param state
     * @param preferences
     */
    setPreferences(state, preferences: OptionResource[]) {
        state.preferences = preferences;
    },

    /**
     * Set settings in state.
     * @param state
     * @param settings
     */
    setSettings(state, settings: OptionResource[]) {
        state.settings = settings;
    },

    /**
     * Reset preferences in state.
     *
     * @param state
     */
    resetPreferences(state) {
        state.preferences = [];
    },

    /**
     * Reset settings in state.
     *
     * @param state
     */
    resetSettings(state) {
        state.settings = [];
    },

};
